import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import SearchIcon from '@mui/icons-material/Search'
import services from '../../services'
import { useNotification } from '../../hooks/useNotification.js'
import VinculoDialog from '../componentes/VinculoDialog'
import DarAcessoDialog from '../componentes/DarAcessoDialog'
import { plural, podeVerFicha, SEM_FICHA } from './comum'
import { useAuth } from '../../hooks/useAuth'
import './gestao.scss'

export interface PessoaLinha { id: number; nome: string; email: string; empresa?: string | null; interna: boolean; ativa: boolean; bloqueada: boolean; equipes: string[]; dashboards: number }
export interface EquipeResumo { id: number; nome: string; membros: number; dashboards: number }

const TAMANHO = 50

export function Situacao({ p }: { p: { ativa: boolean; bloqueada: boolean } }) {
  if (!p.ativa) return <span className="ng-pill inativo">Inativa</span>
  if (p.bloqueada) return <span className="ng-pill alerta">Bloqueada</span>
  return <span className="ng-pill ok">Ativa</span>
}

/** Gestão › Pessoas: quem usa o portal, com equipes e acessos. Dados de login ficam no AuthManager. */
export default function Pessoas() {
  const navigate = useNavigate()
  const { notify } = useNotification()
  const { getUserRoles } = useAuth()
  const verFicha = podeVerFicha(getUserRoles())
  const [busca, setBusca] = useState('')
  const [tipo, setTipo] = useState('')
  const [situacao, setSituacao] = useState('ativa')
  const [equipe, setEquipe] = useState('')
  const [pagina, setPagina] = useState(1)
  const [dados, setDados] = useState<{ total: number; pessoas: PessoaLinha[] } | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [equipes, setEquipes] = useState<EquipeResumo[]>([])
  const [sel, setSel] = useState<Record<number, PessoaLinha>>({})
  const [loteEquipes, setLoteEquipes] = useState(false)
  const [loteAcesso, setLoteAcesso] = useState(false)

  const carregar = useCallback(async () => {
    const r = await services.api.novoPortalPessoas({ busca: busca.trim(), tipo, situacao, equipe, pagina, tamanho: TAMANHO })
    if (r.fail) { setErro(r.error || 'Não foi possível carregar as pessoas.'); return }
    setErro(null)
    setDados(r.data)
  }, [busca, tipo, situacao, equipe, pagina])

  useEffect(() => {
    const t = setTimeout(carregar, 300)
    return () => clearTimeout(t)
  }, [carregar])
  useEffect(() => { setPagina(1) }, [busca, tipo, situacao, equipe])
  useEffect(() => { services.api.novoPortalEquipes().then((r: any) => { if (!r.fail) setEquipes(r.data || []) }) }, [])

  const selecionadas = Object.values(sel)
  const todasNaPagina = !!dados?.pessoas.length && dados.pessoas.every(p => sel[p.id])
  const marcar = (p: PessoaLinha) => setSel(s => { const n = { ...s }; if (n[p.id]) delete n[p.id]; else n[p.id] = p; return n })
  const marcarPagina = () => setSel(s => {
    const n = { ...s }
    dados?.pessoas.forEach(p => { if (todasNaPagina) delete n[p.id]; else n[p.id] = p })
    return n
  })
  const totalPaginas = dados ? Math.max(1, Math.ceil(dados.total / TAMANHO)) : 1
  const quem = selecionadas.length === 1 ? selecionadas[0].nome : plural(selecionadas.length, 'pessoa', 'pessoas')

  return (
    <div className="ng">
      <div className="ng-cab"><h1>Pessoas</h1></div>
      <p className="ng-explic">Quem usa o portal: equipes, acessos, regras e oneflow de cada pessoa. Criar login, trocar senha e inativar continuam no AuthManager.</p>
      {!verFicha && <p className="ng-explic">{SEM_FICHA}</p>}

      <div className="ng-filtros">
        <label className="ng-busca"><SearchIcon fontSize="small" />
          <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar por nome, e-mail, login ou empresa" aria-label="Buscar pessoa" />
        </label>
        <select className="ng-select" value={tipo} onChange={e => setTipo(e.target.value)} aria-label="Tipo">
          <option value="">Internas e clientes</option><option value="interna">Internas</option><option value="cliente">Clientes</option>
        </select>
        <select className="ng-select" value={situacao} onChange={e => setSituacao(e.target.value)} aria-label="Situação">
          <option value="">Todas as situações</option><option value="ativa">Ativas</option><option value="bloqueada">Bloqueadas</option><option value="inativa">Inativas</option>
        </select>
        <select className="ng-select" value={equipe} onChange={e => setEquipe(e.target.value)} aria-label="Equipe">
          <option value="">Todas as equipes</option>
          {equipes.map(e => <option key={e.id} value={e.id}>{e.nome}</option>)}
        </select>
      </div>

      {selecionadas.length > 0 && (
        <div className="ng-barra-sel">
          <b>{plural(selecionadas.length, 'pessoa selecionada', 'pessoas selecionadas')}</b>
          <button type="button" className="ng-btn" onClick={() => setLoteEquipes(true)}>Adicionar a equipes</button>
          <button type="button" className="ng-btn" onClick={() => setLoteAcesso(true)}>Dar acesso a dashboards</button>
          <button type="button" className="ng-btn" style={{ marginLeft: 'auto' }} onClick={() => setSel({})}>Limpar seleção</button>
        </div>
      )}

      <div className="ng-cartao" style={{ padding: '6px 10px' }}>
        <div className="ng-tabela">
          <table>
            <thead><tr>
              <th style={{ width: 36 }}><input type="checkbox" checked={todasNaPagina} onChange={marcarPagina} aria-label="Marcar todas desta página" /></th>
              <th>Pessoa</th><th>Empresa</th><th>Equipes</th><th className="n">Dashboards</th><th>Situação</th>
            </tr></thead>
            <tbody>
              {erro && <tr><td colSpan={6} className="vazio">{erro}</td></tr>}
              {!dados && !erro && <tr><td colSpan={6} className="vazio">Carregando…</td></tr>}
              {dados && dados.pessoas.length === 0 && <tr><td colSpan={6} className="vazio">Ninguém com esses filtros.</td></tr>}
              {dados?.pessoas.map(p => (
                <tr key={p.id} className={verFicha ? 'clicavel' : undefined} onClick={verFicha ? () => navigate(`/app/gestao/pessoas/${p.id}`) : undefined} style={sel[p.id] ? { background: 'var(--np-suave, #fbe8fb)' } : undefined}>
                  <td onClick={e => e.stopPropagation()}><input type="checkbox" checked={!!sel[p.id]} onChange={() => marcar(p)} aria-label={`Selecionar ${p.nome}`} /></td>
                  <td><b>{p.nome}</b><small style={{ display: 'block', color: '#8b92a1' }}>{p.email}</small></td>
                  <td>{p.empresa} <span className="ng-tag">{p.interna ? 'Interna' : 'Cliente'}</span></td>
                  <td>{p.equipes.length ? (p.equipes.length > 2 ? `${p.equipes.slice(0, 2).join(', ')} e mais ${p.equipes.length - 2}` : p.equipes.join(', ')) : <span style={{ color: '#8b92a1' }}>Nenhuma</span>}</td>
                  <td className="n">{p.dashboards}</td>
                  <td><Situacao p={p} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {dados && dados.total > TAMANHO && (
          <div className="ng-paginas">
            <button type="button" className="ng-btn" disabled={pagina <= 1} onClick={() => setPagina(p => p - 1)}>Anterior</button>
            <span>Página {pagina} de {totalPaginas} · {dados.total} pessoas</span>
            <button type="button" className="ng-btn" disabled={pagina >= totalPaginas} onClick={() => setPagina(p => p + 1)}>Próxima</button>
          </div>
        )}
      </div>

      <VinculoDialog
        aberto={loteEquipes}
        titulo="Adicionar a equipes"
        sub={`${quem}`}
        abas={[{ id: 'e', rotulo: 'Equipes', dica: 'Buscar equipe', unidade: ['equipe', 'equipes'], itens: equipes.map(e => ({ id: String(e.id), nome: e.nome, sub: plural(e.membros, 'pessoa', 'pessoas') })) }]}
        resumo={(s, total) => total ? <><b>{quem}</b> {selecionadas.length === 1 ? 'entra' : 'entram'} em <b>{plural(total, 'equipe', 'equipes')}</b> e passam a ver o que elas veem.</> : <>Quem entra numa equipe vê todos os dashboards dela.</>}
        botao={total => total ? `Adicionar a ${plural(total, 'equipe', 'equipes')}` : 'Adicionar'}
        aoFechar={() => setLoteEquipes(false)}
        aoConfirmar={async s => {
          const pares = (s.e || []).flatMap(e => selecionadas.map(p => ({ teamId: Number(e.id), userId: p.id })))
          const r = await services.api.vicularUsuarioComEquipeMult(pares)
          if (r.fail) return r.error || 'Não foi possível adicionar às equipes.'
          setLoteEquipes(false)
          notify('Pessoas adicionadas às equipes.', { variant: 'success' })
          carregar()
          return null
        }}
      />

      <DarAcessoDialog aberto={loteAcesso} pessoas={selecionadas.map(p => p.id)} quem={quem} aoFechar={() => setLoteAcesso(false)}
                       aoDar={m => { setLoteAcesso(false); notify(m, { variant: 'success' }); carregar() }} />
    </div>
  )
}
